import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, authenticateAnonymously } from '../firebase';
import { Game, Team, TEAMS } from '../types';

interface ScheduleViewProps {
  team?: Team;
  isClubSchedule?: boolean;
  onLeagueNameLoad?: (leagueName: string) => void;
  filter?: 'upcoming' | 'finished';
  onGameSelect?: (game: Game | null) => void;
}

interface GameDay {
  date: string;
  isoDate: string;
  games: (Game & { isFortuna?: boolean; leagueName?: string; teamName?: string })[];
}

export const ScheduleView: React.FC<ScheduleViewProps> = ({ team, isClubSchedule = false, onLeagueNameLoad, filter: externalFilter, onGameSelect }) => {
  const [groupedGameDays, setGroupedGameDays] = useState<GameDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'finished'>('all');

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const setupRealtimeListener = async () => {
      try {
        setLoading(true);

        // Erst authentifizieren
        const authSuccess = await authenticateAnonymously();
        if (!authSuccess) {
          setError('Authentifizierung fehlgeschlagen');
          setLoading(false);
          return;
        }

        const scheduleCollection = isClubSchedule ? 'clubSchedule' : (team?.scheduleCollection || '');
        const scheduleRef = doc(db, 'schedule', scheduleCollection);

        // Real-time listener
        unsubscribe = onSnapshot(scheduleRef, (doc) => {
          if (doc.exists()) {
            const data = doc.data();

            // Neue Datenstruktur: schedule Array mit matches
            const scheduleData = data.schedule || [];

            // Extrahiere Liga-Namen aus dem ersten Spiel
            let extractedLeagueName = '';
            if (scheduleData.length > 0 && scheduleData[0].matches && scheduleData[0].matches.length > 0) {
              extractedLeagueName = scheduleData[0].matches[0].leagueName || '';
            }

            // Callback für Parent-Component
            if (onLeagueNameLoad && extractedLeagueName) {
              onLeagueNameLoad(extractedLeagueName);
            }

            // Helfer-Funktion um Team-Namen zu ermitteln (nur für Club Schedule)
            const getTeamInfo = (homeTeam: string, awayTeam: string) => {
              const fortunaHome = homeTeam?.toLowerCase().includes('fortuna');
              const fortunaAway = awayTeam?.toLowerCase().includes('fortuna');

              if (fortunaHome) {
                const team = TEAMS.find(t => homeTeam?.includes(t.name.split(' ')[1]));
                return { teamName: team?.name || 'Fortuna Köln', isHome: true };
              } else if (fortunaAway) {
                const team = TEAMS.find(t => awayTeam?.includes(t.name.split(' ')[1]));
                return { teamName: team?.name || 'Fortuna Köln', isHome: false };
              }
              return { teamName: 'Fortuna Köln', isHome: true };
            };

            // Gruppiere Spieltage mit allen Spielen
            const groupedGameDays = scheduleData.map((gameDay: any) => {
              const games = gameDay.matches ? gameDay.matches.map((match: any) => {
                const teamInfo = isClubSchedule ? getTeamInfo(match.home, match.away) : null;
                const hasScore = match.goalsHome !== undefined && match.goalsHome !== null &&
                                match.goalsAway !== undefined && match.goalsAway !== null;
                const hasResult = match.result !== undefined && match.result !== null;
                const isFinished = hasResult || hasScore;

                return {
                  id: match.id || `${gameDay.isoDate}-${match.time}`,
                  date: gameDay.isoDate,
                  time: match.time,
                  homeTeam: match.home,
                  awayTeam: match.away,
                  homeScore: match.goalsHome,
                  awayScore: match.goalsAway,
                  location: match.location,
                  locationId: match.locationId,
                  finished: isFinished,
                  live: match.live || false,
                  isFortuna: !isClubSchedule ? (match.home?.includes('Fortuna') || match.away?.includes('Fortuna')) : undefined,
                  leagueName: isClubSchedule ? match.leagueName : undefined,
                  teamName: teamInfo?.teamName,
                  pdfUrl: match.pdfUrl,
                  refs: match.refs
                };
              }) : [];

              return {
                date: gameDay.date,
                isoDate: gameDay.isoDate,
                games: games
              };
            });

            setGroupedGameDays(groupedGameDays);
            setError(null);
          } else {
            setError(isClubSchedule ? 'Vereinsspielplan nicht gefunden' : 'Spielplan nicht gefunden');
          }
          setLoading(false);
        }, (err) => {
          setError(isClubSchedule ? 'Fehler beim Laden des Vereinsspielplans' : 'Fehler beim Laden des Spielplans');
          console.error('Error with schedule listener:', err);
          setLoading(false);
        });

      } catch (err) {
        setError(isClubSchedule ? 'Fehler beim Setup des Vereinsspielplans' : 'Fehler beim Setup des Spielplans');
        console.error('Error setting up schedule listener:', err);
        setLoading(false);
      }
    };

    setupRealtimeListener();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [team, isClubSchedule, onLeagueNameLoad]);

  // Filtere und gruppiere Spieltage
  const currentFilter = externalFilter || filter;
  const today = new Date().toISOString().split('T')[0];

  const filteredGameDays = groupedGameDays.map(gameDay => ({
    ...gameDay,
    games: gameDay.games.filter(game => {
      if (currentFilter === 'upcoming') {
        if (gameDay.isoDate === today) return !game.finished;
        return gameDay.isoDate >= today;
      }
      if (currentFilter === 'finished') {
        if (gameDay.isoDate === today) return game.finished;
        return gameDay.isoDate < today;
      }
      return true;
    })
  })).filter(gameDay => gameDay.games.length > 0)
  // Sortierung: Bei finished (Ergebnisse) absteigend, bei upcoming aufsteigend
  .sort((a, b) => {
    if (currentFilter === 'finished') {
      return b.isoDate.localeCompare(a.isoDate); // Neueste zuerst
    } else {
      return a.isoDate.localeCompare(b.isoDate); // Älteste zuerst
    }
  });

  if (loading) {
    return (
      <div>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fortuna-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">{isClubSchedule ? 'Lade Vereinsspielplan...' : 'Lade Spielplan...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="text-center py-8 text-red-600">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {!externalFilter && (
        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded text-sm ${
              filter === 'all'
                ? 'bg-fortuna-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Alle
          </button>
          <button
            onClick={() => setFilter('upcoming')}
            className={`px-3 py-1 rounded text-sm ${
              filter === 'upcoming'
                ? 'bg-fortuna-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Spielplan
          </button>
          <button
            onClick={() => setFilter('finished')}
            className={`px-2 py-1 rounded text-sm ${
              filter === 'finished'
                ? 'bg-fortuna-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Ergebnisse
          </button>
        </div>
      )}

      <div className="space-y-6">
        {filteredGameDays.map((gameDay, dayIndex) => (
          <div
            key={gameDay.isoDate}
            className="space-y-2"
          >
            {/* Spieltag Header */}
            <div className="bg-gray-100 px-3 py-2 rounded font-medium text-gray-700 text-sm">
              {gameDay.date}
            </div>

            {/* Spiele des Tages */}
            <div className="space-y-1">
              {gameDay.games.map((game, gameIndex) => (
                <div
                  key={game.id || gameIndex}
                  onClick={() => {
                    if (onGameSelect) {
                      onGameSelect(game);
                    }
                  }}
                  className={`${isClubSchedule ? 'p-3' : 'p-2'} rounded border text-xs ${
                    !isClubSchedule && game.isFortuna
                      ? 'bg-fortuna-50 border-fortuna-200 font-medium'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  } ${onGameSelect ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                >
                  {/* Desktop/Tablet Layout: Single Row */}
                  <div className={`hidden ${isClubSchedule ? 'min-[500px]:flex' : 'min-[600px]:flex'} items-center justify-between`}>
                    {/* Zeit */}
                    <div className={`${isClubSchedule ? 'w-20' : 'w-16'} text-gray-600 whitespace-nowrap`}>
                      {game.time} Uhr
                    </div>

                    {/* Liga und Begegnung */}
                    <div className={`flex-1 ${isClubSchedule ? 'text-center' : 'flex items-center justify-center'}`}>
                      {isClubSchedule && game.leagueName && (
                        <div className="text-xs text-gray-500 truncate mb-1" title={game.leagueName}>
                          {game.leagueName}
                        </div>
                      )}
                      <div className={`flex items-center justify-center ${isClubSchedule ? 'space-x-3' : ''}`}>
                        <div className={`${isClubSchedule ? 'text-right' : 'text-left w-3/6'} flex-1 truncate ${
                          game.finished && game.homeScore !== undefined && game.awayScore !== undefined && game.homeScore > game.awayScore ? 'font-bold' : ''
                        }`} title={game.homeTeam}>
                          {game.homeTeam}
                        </div>
                        <div className={`${isClubSchedule ? '' : 'text-center w-5'} text-gray-400`}>-</div>
                        <div className={`text-left ${isClubSchedule ? '' : 'w-3/6'} flex-1 truncate ${
                          game.finished && game.homeScore !== undefined && game.awayScore !== undefined && game.awayScore > game.homeScore ? 'font-bold' : ''
                        }`} title={game.awayTeam}>
                          {game.awayTeam}
                        </div>
                      </div>
                    </div>

                    {/* Ergebnis mit Live-Indikator */}
                    <div className={`text-center ${isClubSchedule ? 'w-20' : 'w-20'}`}>
                      {game.live && (
                        <div className="flex items-center justify-center mb-1">
                          <span className="text-red-600 text-xs font-medium">Live</span>
                          <div className="w-1.5 h-1.5 bg-red-600 rounded-full ml-1 animate-pulse"></div>
                        </div>
                      )}
                      {game.homeScore !== undefined && game.homeScore !== null && game.awayScore !== undefined && game.awayScore !== null ? (
                        <span className={`font-bold ${game.live ? 'text-red-600' : 'text-gray-900'}`}>
                          {game.homeScore}:{game.awayScore}
                        </span>
                      ) : game.refs ? (
                        <span className="text-gray-600 text-xs truncate block px-1" title={game.refs}>
                          {game.refs}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">
                        </span>
                      )}
                    </div>

                    {/* Detail Indikator */}
                    {onGameSelect && (
                      <div className="w-6 flex items-center justify-center">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Mobile Layout: Two Rows */}
                  <div className={isClubSchedule ? 'min-[500px]:hidden' : 'min-[600px]:hidden'}>
                    {/* Erste Zeile: Zeit, Liga und Live-Indikator */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-600 text-xs">{game.time} Uhr</span>
                        {isClubSchedule && game.leagueName && (
                          <div className="text-xs text-gray-500 truncate" title={game.leagueName}>
                            {game.leagueName}
                          </div>
                        )}
                      </div>
                      {game.live && (
                        <div className="flex items-center">
                          <span className="text-red-600 text-xs font-medium">Live</span>
                          <div className="w-1.5 h-1.5 bg-red-600 rounded-full ml-1 animate-pulse"></div>
                        </div>
                      )}
                    </div>

                    {/* Zweite Zeile: Teams und Ergebnis */}
                    <div className="flex items-center gap-3">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <span className={`truncate flex-1 ${
                          game.finished && game.homeScore !== undefined && game.awayScore !== undefined && game.homeScore > game.awayScore ? 'font-bold' : ''
                        }`} title={game.homeTeam}>
                          {game.homeTeam}
                        </span>
                        <span className="text-gray-400 flex-shrink-0">-</span>
                        <span className={`truncate flex-1 ${
                          game.finished && game.homeScore !== undefined && game.awayScore !== undefined && game.awayScore > game.homeScore ? 'font-bold' : ''
                        }`} title={game.awayTeam}>
                          {game.awayTeam}
                        </span>
                      </div>
                      {game.homeScore !== undefined && game.homeScore !== null && game.awayScore !== undefined && game.awayScore !== null ? (
                        <div className="flex-shrink-0">
                          <span className={`font-bold ${game.live ? 'text-red-600' : 'text-gray-900'}`}>
                            {game.homeScore}:{game.awayScore}
                          </span>
                        </div>
                      ) : game.refs ? (
                        <div className="flex-shrink-0 max-w-[80px]">
                          <span className="text-gray-600 text-xs truncate block" title={game.refs}>
                            {game.refs}
                          </span>
                        </div>
                      ) : null}
                      {onGameSelect && (
                        <div className="flex-shrink-0">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {filteredGameDays.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {currentFilter === 'upcoming' ? 'Keine kommenden Spiele' :
             currentFilter === 'finished' ? 'Keine beendeten Spiele' :
             'Keine Spiele gefunden'}
          </div>
        )}
      </div>
    </div>
  );
};